/* eslint-disable */

import React from "react";
import moment from "moment";
import {
    Area,
    ComposedChart,
    Label,
    Legend,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Spinner } from "react-bootstrap";

import TimelineHelper from "./timeline_helper";
import { MEASUREMENTS_META } from "./graph_settings";
import cropr from "../shared/utils/cropr";
import gettext from "../shared/utils/gettext";
import { _ } from "../shared/utils/legacy";

class PlantingSeedingTimeline extends React.Component {
  state = {
    ticks: null,
    graphDomain: [],
    soilTempData: [],
    plantingSeedingAdvicesData: [],
  };

  componentDidMount() {
    let graphDomain = this.initTimeline();
    this.loadData(graphDomain);
  }

  initTimeline() {
    if (cropr && cropr.l10n) moment.locale(cropr.l10n.languageCode);
    const ticks = TimelineHelper.getFirstDaysOfMonths(
      this.props.cropfield.getDate("period_start"),
      this.props.cropfield.getDate("period_end")
    );
    this.records = this.getRecords();
    let graphDomain = [ticks[0], ticks[ticks.length - 1]];
    this.setState({
      ticks: ticks,
      graphDomain: graphDomain,
    });
    return graphDomain;
  }

  getRecords() {
    let records = cropr.records.filter(
      (record) =>
        record.get("processing_state") === "performed" &&
        record.get("cropfield") === this.props.cropfield.id &&
        record
    );
    records = _.filter(records, (r) => _.filter(r.get("activities"), (a) => a.theme === 6).length);
    return records;
  }

  async loadData(graphDomain) {
    this.setState({
      recordingData: TimelineHelper.loadRecordData(this.records),
    });
    this.loadSoilData(graphDomain);
    this.loadSowingAdvice(graphDomain);
  }

  loadSoilData(graphDomain) {
    const cropField = this.props.cropfield;
    const soilTempCacheKeyData = `soilTempData_${cropField.id}}`;
    if (cropr[soilTempCacheKeyData]) {
      this.setState({ soilTempData: cropr[soilTempCacheKeyData] });
      return; // no need to fetch data again.
    }

    const start_date = cropField.getDate('period_start').format('YYYY-MM-DD');
    const end_date = cropField.getDate('period_end').format('YYYY-MM-DD');
    const centroid = cropField.getCentroid(4326);
    if (centroid) {
        const {y: lat, x: lng} = (centroid || {});
        cropr.d2c.apiClient.get({
            url: `/measurements/daily/`,
            format: 'json',
            meta: 'ST__depths__10',
            lat: lat,
            lng: lng,
            start_date: start_date,
            end_date: end_date,
            success: (data) => {
                if (!data) return;
                cropr[soilTempCacheKeyData] = []
                Object.entries(data).forEach(([day, values]) => {
                    cropr[soilTempCacheKeyData].push({
                        date: moment(day, "YYYY-MM-DD").unix(),
                        soil_temp: values.ST__depths__10,
                    })
                })
                this.setState({ soilTempData: cropr[soilTempCacheKeyData] });
            },
            error: (response) => {
                console.warn(`Could not fetch soil temperature. Cause: ${response.status}`);
            }
        });
    }
  }

  loadSowingAdvice(graphDomain) {
    const cropField = this.props.cropfield;
    const sowingAdviceDatesCacheKeyData = `sowingAdviceDatesData_${cropField.id}}`;
    if (cropr[sowingAdviceDatesCacheKeyData]) {
      this.setState({ plantingSeedingAdvicesData: cropr[sowingAdviceDatesCacheKeyData] });
      return; // no need to fetch data again.
    }
    const farm = cropField.getRelated("farm");
    const plan = cropField.getRelated("plan");
    let url = `/api/v3/farms/${farm.did}/cropping_schemes/${plan.get("did")}/crop_fields/${cropField.get("did")}/sowing_advice/`;
    fetch(url, {
      method: "GET",
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          console.warn(`error fetching sowing advice data from: ${url}!`);
        }
      })
      .then((soil_data) => {
        if (soil_data.sowing_advice && soil_data.sowing_advice.sowing_dates) {
          cropr[sowingAdviceDatesCacheKeyData] = soil_data.sowing_advice.sowing_dates.map(
            (sowing_advice_date) => ({
              date: moment(sowing_advice_date, "YYYY-MM-DD").unix(),
              sowing_advice: 0,
            })
          );
          this.setState({
            plantingSeedingAdvicesData: cropr[sowingAdviceDatesCacheKeyData],
          });
        }
      })
      .catch((err) => {
        console.warn(`error fetching sowing from: ${url}, cause: ${err}!`);
      });
  }

  render() {
    const records = cropr.records.filter(
      (record) =>
        record.get("processing_state") === "performed" &&
        record.get("cropfield") === this.props.cropfield.id
    );
    const plantRecordDate = TimelineHelper.getPlantDate(records);
    const harvestRecordDate = TimelineHelper.getHarvestDate(records);
    let graph_data = [
      ...(this.state.soilTempData || []),
      ...(this.state.plantingSeedingAdvicesData || []),
      ...(this.state.recordingData || []),
    ];
    return (
      <div className="card">
        <div className="card-body">
          <div className="card-content">
            {this.state.ticks ? (
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart allowDataOverflow data={graph_data}>
                  <YAxis type="number" yAxisId={0} hide />
                  <YAxis
                    label={MEASUREMENTS_META.soil_temperature.unit}
                    domain={MEASUREMENTS_META.soil_temperature.domain}
                    stroke={MEASUREMENTS_META.soil_temperature.stroke}
                    allowDataOverflow={MEASUREMENTS_META.soil_temperature.clip}
                    type="number"
                    yAxisId={1}
                    hide
                  />
                  <XAxis
                    dataKey="date"
                    domain={this.state.graphDomain}
                    name="Date"
                    tickFormatter={(date) => moment.unix(date).format("LL")}
                    type="number"
                    scale="time"
                    ticks={this.state.ticks}
                    interval={0}
                    padding={{ left: 30, right: 30 }}
                  />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={TimelineHelper.renderTooltip(this.records, graph_data)}
                  />
                  <Legend align="right" verticalAlign="top" iconSize={10} />
                  <ReferenceLine y={10} yAxisId={1} stroke="#A9B6BA" strokeDasharray="3 3">
                    <Label value="10 &#8451;" position="insideTopRight" />
                  </ReferenceLine>
                  <ReferenceLine x={moment().unix()} stroke="#A9B6BA" strokeDasharray="3 3">
                    <Label value={gettext("now")} position="insideTopRight" />
                  </ReferenceLine>
                  {plantRecordDate && (
                    <ReferenceLine
                      x={moment(plantRecordDate, "DD-MM-YYYY").unix()}
                      stroke="#A9B6BA"
                      strokeDasharray="3 3"
                    >
                      <Label value={gettext("Plant")} position="insideTopRight" />
                    </ReferenceLine>
                  )}
                  {harvestRecordDate && (
                    <ReferenceLine
                      x={moment(harvestRecordDate, "DD-MM-YYYY").unix()}
                      stroke="#A9B6BA"
                      strokeDasharray="3 3"
                    >
                      <Label value={gettext("Harvest")} position="insideTopRight" />
                    </ReferenceLine>
                  )}
                  <Line
                    isAnimationActive={false}
                    name={gettext("Planting/Sowing advice dates")}
                    dataKey="sowing_advice"
                    legendType="rect"
                    strokeWidth={0}
                    stroke="#20AD6B"
                    dot={{ stroke: "#20AD6B", strokeWidth: 5 }}
                    yAxisId={0}
                  />
                  <Area
                    isAnimationActive={false}
                    dot={false}
                    type="monotone"
                    name={gettext("Soil temperature")}
                    dataKey="soil_temp"
                    stroke="none"
                    fill={MEASUREMENTS_META.soil_temperature.stroke}
                    strokeWidth={2}
                    legendType="rect"
                    yAxisId={1}
                  />
                  <Line
                    isAnimationActive={false}
                    name={gettext("Activities")}
                    dataKey="records"
                    legendType="circle"
                    strokeWidth={0}
                    stroke="#F9C342"
                    dot={{ stroke: "#F9C342", strokeWidth: 5 }}
                    yAxisId={0}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ width: "100%", height: "150px", textAlign: "center" }}>
                <Spinner animation="border" role="status">
                  <span className="sr-only">{gettext("Loading")}...</span>
                </Spinner>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default PlantingSeedingTimeline;
